import type { RecentLabHighlight, RecentLabs, RecentLabStatus } from "@/features/patient/dashboard/api/recentLabsApi";

// The "Most Recent Lab Result" widget's show/hide rules. The backend
// (GET /api/patient/dashboard/recent-labs) already picks lab results only and
// the two to highlight; this only refuses to draw anything that isn't a real
// result, so a missing field is never replaced by placeholder text
// ("Unknown", "—", "Lab Value") and a narrative sentence never lands in the
// large number font (the 2026-10-23 bug: exam findings shown as labs).

const STATUSES: readonly RecentLabStatus[] = ["critical_high", "critical_low", "high", "low", "abnormal", "normal", "unknown"];
const NUMBER_RE = /^[<>≤≥]=?\s*-?\d+(?:\.\d+)?$|^-?\d+(?:\.\d+)?$/;
// A qualitative result is a word or two ("Negative", "Not detected", "1+").
const QUALITATIVE_MAX_LENGTH = 24;
const QUALITATIVE_MAX_WORDS = 3;
const SENTENCE_RE = /[,;:.!?]\s|[,;!?]$|\.$/;

const nonEmpty = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

/** A real result: a number, or a short qualitative answer — never a sentence. */
export function isLabResultValue(value: unknown, valueType: unknown): boolean {
  if (!nonEmpty(value)) return false;
  const v = value.trim();
  if (valueType === "numeric") return NUMBER_RE.test(v);
  if (valueType === "qualitative") {
    return v.length <= QUALITATIVE_MAX_LENGTH && v.split(/\s+/).length <= QUALITATIVE_MAX_WORDS && !SENTENCE_RE.test(v);
  }
  return false;
}

/** Whether a highlight can be drawn as a small card: label, a real value and a known status. */
export function isShowableHighlight(h: Partial<RecentLabHighlight> | null | undefined): h is RecentLabHighlight {
  return (
    !!h &&
    nonEmpty(h.label) &&
    isLabResultValue(h.value, h.valueType) &&
    nonEmpty(h.status) &&
    STATUSES.includes(h.status as RecentLabStatus)
  );
}

/** The widget is shown only when the backend found lab results. */
export function hasLabResults(data: RecentLabs | undefined): data is RecentLabs & { date: string } {
  return !!data && nonEmpty(data.date) && data.total > 0;
}

/** 0–100 from the results that could be judged; null when none could (no score shown). */
export function labScore(data: RecentLabs): number | null {
  if (!data.assessed || data.assessed <= 0) return null;
  return Math.round((Math.min(data.inRange, data.assessed) / data.assessed) * 100);
}

const PILL_TEXT: Partial<Record<RecentLabStatus, string>> = {
  critical_high: "Critical High",
  critical_low: "Critical Low",
  high: "High",
  low: "Low",
  abnormal: "Abnormal",
};

/** The status pill's text; null for normal or not assessed (no pill). */
export function labStatusPill(status: RecentLabStatus): string | null {
  return PILL_TEXT[status] ?? null;
}

/** Red for abnormal / critical, amber for high / low (the existing pill colours). */
export function isSevereLabStatus(status: RecentLabStatus): boolean {
  return status === "abnormal" || status === "critical_high" || status === "critical_low";
}
